/**
 * SearchBox - Unified search component for address geocoding, coordinate search,
 * and feature attribute search.
 *
 * Features:
 * - MUI Autocomplete with 300ms debounce
 * - Address geocoding with confidence scores (Req 27.1, 27.3)
 * - Zoom to geocoded location + marker overlay (Req 27.2)
 * - Coordinate parsing (decimal degrees, DMS, UTM) (Req 28.1, 28.2)
 * - Coordinate search and format validation (Req 28.3, 28.4)
 * - Feature attribute search (case-insensitive partial matching) (Req 29.1, 29.4)
 * - Search results with layer and attribute info (Req 29.2)
 * - Zoom to found feature (Req 29.3)
 * - Filter search by layer/field (Req 29.5)
 * - "Add to layer" creates Point feature from geocoded result (Req 27.6, 28.5)
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Place as PlaceIcon,
  MyLocation as CoordIcon,
  FindInPage as FeatureIcon,
  Close as CloseIcon,
  AddLocation as AddLocationIcon,
  FilterList as FilterIcon,
  LocationOn as LocationOnIcon,
  Layers as LayersIcon,
  PinDrop as DigipinIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  setQuery,
  setSearchMode,
  setSearchFilter,
  geocodeAddress,
  searchFeatureAttributes,
  selectGeocodingResult,
  parseCoordinateInput,
  parseDigipinInput,
  clearResults,
  clearMarker,
  type SearchMode,
  type FeatureSearchResult,
  type DigipinSearchResult,
  type SearchFilter,
} from '../store/slices/geocodingSlice';
import type { GeocodingResult } from '../services/GeocodingService';
import { looksLikeCoordinates } from '../services/CoordinateParser';
import { looksLikeDigipin } from '../utils/digipinEncoder';

/** Union type for all search result items displayed in the autocomplete */
type SearchResultItem =
  | { type: 'geocoding'; data: GeocodingResult }
  | { type: 'coordinate'; data: { coordinates: [number, number]; format: string } }
  | { type: 'digipin'; data: DigipinSearchResult }
  | { type: 'feature'; data: FeatureSearchResult };

interface SearchBoxProps {
  /** Callback when user wants to zoom to a location [lon, lat] */
  onZoomToLocation?: (coordinates: [number, number], zoom?: number) => void;
  /** Callback when user wants to zoom to a feature extent */
  onZoomToFeature?: (featureId: string, layerId: string, geometry: GeoJSON.Geometry) => void;
  /** Callback to add a point feature to the active layer */
  onAddToLayer?: (coordinates: [number, number], address?: string) => void;
  /** Callback to place a marker on the map */
  onPlaceMarker?: (coordinates: [number, number] | null) => void;
}

const DEBOUNCE_MS = 300;

const SearchBox: React.FC<SearchBoxProps> = ({
  onZoomToLocation,
  onZoomToFeature,
  onAddToLayer,
  onPlaceMarker,
}) => {
  const dispatch = useAppDispatch();
  const {
    query,
    searchMode,
    geocodingResults,
    featureSearchResults,
    coordinateResult,
    digipinResult,
    isLoading,
    error,
    searchFilter,
    markerCoordinates,
  } = useAppSelector((state) => state.geocoding);

  const layers = useAppSelector((state) => state.layers.layers);
  const visibleLayers = useMemo(() => layers.filter((l) => l.visible), [layers]);

  const [inputValue, setInputValue] = useState(query);
  const [showFilters, setShowFilters] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search execution
  const executeSearch = useCallback(
    (value: string) => {
      if (!value.trim()) {
        dispatch(clearResults());
        return;
      }

      dispatch(setQuery(value));

      // Auto-detect DIGIPIN input
      if (looksLikeDigipin(value)) {
        dispatch(parseDigipinInput(value));
        return;
      }

      // Auto-detect coordinate input
      if (looksLikeCoordinates(value)) {
        dispatch(parseCoordinateInput(value));
        return;
      }

      switch (searchMode) {
        case 'address':
          dispatch(geocodeAddress(value));
          break;
        case 'coordinate':
          dispatch(parseCoordinateInput(value));
          break;
        case 'digipin':
          dispatch(parseDigipinInput(value));
          break;
        case 'feature':
          dispatch(searchFeatureAttributes({ query: value, filter: searchFilter }));
          break;
      }
    },
    [dispatch, searchMode, searchFilter]
  );

  // Handle input change with 300ms debounce
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, value: string) => {
      setInputValue(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        executeSearch(value);
      }, DEBOUNCE_MS);
    },
    [executeSearch]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Build options list from current results
  const options: SearchResultItem[] = useMemo(() => {
    const items: SearchResultItem[] = [];

    // Add DIGIPIN result if valid
    if (digipinResult) {
      items.push({
        type: 'digipin',
        data: digipinResult,
      });
    }

    // Add coordinate result if valid
    if (coordinateResult && coordinateResult.valid) {
      items.push({
        type: 'coordinate',
        data: {
          coordinates: coordinateResult.coordinates,
          format: coordinateResult.format,
        },
      });
    }

    // Add geocoding results
    for (const result of geocodingResults) {
      items.push({ type: 'geocoding', data: result });
    }

    // Add feature search results
    for (const result of featureSearchResults) {
      items.push({ type: 'feature', data: result });
    }

    return items;
  }, [digipinResult, coordinateResult, geocodingResults, featureSearchResults]);

  // Handle result selection
  const handleSelect = useCallback(
    (_event: React.SyntheticEvent, value: SearchResultItem | null) => {
      if (!value) return;

      switch (value.type) {
        case 'digipin': {
          const coords = value.data.coordinates;
          onPlaceMarker?.(coords);
          onZoomToLocation?.(coords, 18); // Zoom closely to 4m grid cell
          break;
        }
        case 'geocoding': {
          const result = value.data;
          dispatch(selectGeocodingResult(result));
          onPlaceMarker?.(result.coordinates);
          onZoomToLocation?.(result.coordinates, 16);
          break;
        }
        case 'coordinate': {
          const coords = value.data.coordinates;
          onPlaceMarker?.(coords);
          onZoomToLocation?.(coords, 16);
          break;
        }
        case 'feature': {
          const feature = value.data;
          onZoomToFeature?.(feature.featureId, feature.layerId, feature.geometry);
          break;
        }
      }
    },
    [dispatch, onZoomToLocation, onZoomToFeature, onPlaceMarker]
  );

  // Handle mode change
  const handleModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newMode: SearchMode | null) => {
      if (newMode) {
        dispatch(setSearchMode(newMode));
        setInputValue('');
      }
    },
    [dispatch]
  );

  // Handle filter changes
  const handleLayerFilterChange = useCallback(
    (layerId: string) => {
      dispatch(setSearchFilter({ ...searchFilter, layerId: layerId || undefined }));
    },
    [dispatch, searchFilter]
  );

  const handleFieldFilterChange = useCallback(
    (fieldName: string) => {
      dispatch(setSearchFilter({ ...searchFilter, fieldName: fieldName || undefined }));
    },
    [dispatch, searchFilter]
  );

  // Get available fields for the selected layer filter
  const availableFields = useMemo(() => {
    if (!searchFilter.layerId) {
      // Collect all fields from all visible layers
      const fields = new Set<string>();
      for (const layer of visibleLayers) {
        if (layer.features) {
          for (const feature of layer.features) {
            if (feature.properties) {
              Object.keys(feature.properties).forEach((k) => fields.add(k));
            }
          }
        }
      }
      return Array.from(fields).sort();
    }
    const layer = visibleLayers.find((l) => l.id === searchFilter.layerId);
    if (!layer?.features) return [];
    const fields = new Set<string>();
    for (const feature of layer.features) {
      if (feature.properties) {
        Object.keys(feature.properties).forEach((k) => fields.add(k));
      }
    }
    return Array.from(fields).sort();
  }, [visibleLayers, searchFilter.layerId]);

  // Handle "Add to Layer" action
  const handleAddToLayer = useCallback(() => {
    if (markerCoordinates) {
      const address = geocodingResults.length > 0 ? geocodingResults[0].address : undefined;
      onAddToLayer?.(markerCoordinates, address);
    }
  }, [markerCoordinates, geocodingResults, onAddToLayer]);

  // Clear search
  const handleClear = useCallback(() => {
    setInputValue('');
    dispatch(clearResults());
    onPlaceMarker?.(null);
  }, [dispatch, onPlaceMarker]);

  // Get option label for display
  const getOptionLabel = useCallback((option: SearchResultItem): string => {
    switch (option.type) {
      case 'digipin':
        return `DIGIPIN: ${option.data.digipin} (${option.data.latitude.toFixed(6)}, ${option.data.longitude.toFixed(6)})`;
      case 'geocoding':
        return option.data.address;
      case 'coordinate':
        return `${option.data.coordinates[1].toFixed(6)}, ${option.data.coordinates[0].toFixed(6)} (${option.data.format})`;
      case 'feature':
        return `${option.data.matchingValue} — ${option.data.layerName}`;
    }
  }, []);

  // Render option in dropdown
  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: SearchResultItem) => {
      const { key, ...restProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
      switch (option.type) {
        case 'digipin':
          return (
            <li key={key} {...restProps}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <DigipinIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.85rem', color: '#ffffff', fontWeight: 600 }}
                    >
                      DIGIPIN: {option.data.digipin}
                    </Typography>
                    <Chip
                      label="4m Grid"
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: '0.6rem',
                        backgroundColor: 'rgba(245,158,11,0.2)',
                        color: '#f59e0b',
                        border: '1px solid rgba(245,158,11,0.4)',
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#aaaaaa', fontSize: '0.7rem' }}>
                    Lat: {option.data.latitude.toFixed(6)}°, Lon: {option.data.longitude.toFixed(6)}
                    °
                  </Typography>
                </Box>
              </Box>
            </li>
          );
        case 'geocoding':
          return (
            <li key={key} {...restProps}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                <LocationOnIcon sx={{ fontSize: 18, color: '#4fc3f7', mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.8rem', color: '#e0e0e0', lineHeight: 1.3 }}
                    noWrap
                  >
                    {option.data.address}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                    <Chip
                      label={option.data.placeType}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: '0.6rem',
                        backgroundColor: 'rgba(79,195,247,0.15)',
                        color: '#4fc3f7',
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem' }}>
                      Confidence: {(option.data.confidence * 100).toFixed(0)}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </li>
          );
        case 'coordinate':
          return (
            <li key={key} {...restProps}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CoordIcon sx={{ fontSize: 18, color: '#66bb6a' }} />
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#e0e0e0' }}>
                    {option.data.coordinates[1].toFixed(6)}°,{' '}
                    {option.data.coordinates[0].toFixed(6)}°
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem' }}>
                    Format: {option.data.format}
                  </Typography>
                </Box>
              </Box>
            </li>
          );
        case 'feature':
          return (
            <li key={key} {...restProps}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                <FeatureIcon sx={{ fontSize: 18, color: '#ffa726', mt: 0.25 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#e0e0e0' }} noWrap>
                    {option.data.matchingValue}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                    <Chip
                      label={option.data.layerName}
                      size="small"
                      icon={<LayersIcon sx={{ fontSize: '12px !important' }} />}
                      sx={{
                        height: 16,
                        fontSize: '0.6rem',
                        backgroundColor: 'rgba(255,167,38,0.15)',
                        color: '#ffa726',
                        '& .MuiChip-icon': { color: '#ffa726' },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#888', fontSize: '0.65rem' }}>
                      {option.data.matchingField}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </li>
          );
      }
    },
    []
  );

  // Placeholder text based on mode
  const placeholder = useMemo(() => {
    switch (searchMode) {
      case 'address':
        return 'Search address or place...';
      case 'coordinate':
        return 'Enter coordinates (e.g., 40.7128, -74.0060)...';
      case 'digipin':
        return 'Enter DIGIPIN (e.g. 3J8-K82-L914 or 4T396F42L7)...';
      case 'feature':
        return 'Search feature attributes...';
    }
  }, [searchMode]);

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'absolute',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 600,
        width: 420,
        maxWidth: 'calc(100% - 32px)',
        backgroundColor: '#2d2d2d',
        borderRadius: '8px',
        border: '1px solid #3a3a3a',
        overflow: 'visible',
      }}
      data-testid="search-box"
    >
      {/* Search mode toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1,
          pt: 0.5,
          gap: 0.5,
        }}
      >
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: '#888',
              borderColor: '#444',
              fontSize: '0.65rem',
              py: 0.25,
              px: 1,
              textTransform: 'none',
              '&.Mui-selected': {
                color: '#4fc3f7',
                backgroundColor: 'rgba(79,195,247,0.12)',
                borderColor: '#4fc3f7',
              },
            },
          }}
        >
          <ToggleButton value="address" aria-label="Address search">
            <PlaceIcon sx={{ fontSize: 14, mr: 0.5 }} />
            Address
          </ToggleButton>
          <ToggleButton value="coordinate" aria-label="Coordinate search">
            <CoordIcon sx={{ fontSize: 14, mr: 0.5 }} />
            Coordinate
          </ToggleButton>
          <ToggleButton value="digipin" aria-label="DIGIPIN search">
            <DigipinIcon
              sx={{
                fontSize: 14,
                mr: 0.5,
                color: searchMode === 'digipin' ? '#f59e0b' : 'inherit',
              }}
            />
            DIGIPIN
          </ToggleButton>
          <ToggleButton value="feature" aria-label="Feature search">
            <FeatureIcon sx={{ fontSize: 14, mr: 0.5 }} />
            Feature
          </ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        {/* Filter toggle (only for feature search) */}
        {searchMode === 'feature' && (
          <Tooltip title="Filter by layer/field">
            <IconButton
              size="small"
              onClick={() => setShowFilters((v) => !v)}
              sx={{
                color: showFilters ? '#4fc3f7' : '#888',
                width: 24,
                height: 24,
              }}
            >
              <FilterIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Clear button */}
        {inputValue && (
          <Tooltip title="Clear search">
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ color: '#888', width: 24, height: 24 }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Filter controls for feature search */}
      {searchMode === 'feature' && showFilters && (
        <Box sx={{ px: 1, py: 0.5, display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel
              sx={{ color: '#888', fontSize: '0.7rem', '&.Mui-focused': { color: '#4fc3f7' } }}
            >
              Layer
            </InputLabel>
            <Select
              value={searchFilter.layerId || ''}
              onChange={(e) => handleLayerFilterChange(e.target.value)}
              label="Layer"
              sx={{
                color: '#e0e0e0',
                fontSize: '0.75rem',
                height: 30,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4fc3f7' },
              }}
            >
              <MenuItem value="">
                <em>All visible layers</em>
              </MenuItem>
              {visibleLayers.map((layer) => (
                <MenuItem key={layer.id} value={layer.id}>
                  {layer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel
              sx={{ color: '#888', fontSize: '0.7rem', '&.Mui-focused': { color: '#4fc3f7' } }}
            >
              Field
            </InputLabel>
            <Select
              value={searchFilter.fieldName || ''}
              onChange={(e) => handleFieldFilterChange(e.target.value)}
              label="Field"
              sx={{
                color: '#e0e0e0',
                fontSize: '0.75rem',
                height: 30,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#666' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4fc3f7' },
              }}
            >
              <MenuItem value="">
                <em>All fields</em>
              </MenuItem>
              {availableFields.map((field) => (
                <MenuItem key={field} value={field}>
                  {field}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Autocomplete search input */}
      <Box sx={{ px: 1, pb: 1, pt: 0.5 }}>
        <Autocomplete
          freeSolo
          options={options}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onChange={handleSelect}
          getOptionLabel={getOptionLabel}
          renderOption={renderOption}
          filterOptions={(x) => x} // Don't filter client-side, results come from API/store
          loading={isLoading}
          noOptionsText={error || 'Type to search...'}
          clearOnBlur={false}
          PaperComponent={(props) => (
            <Paper
              {...props}
              sx={{
                backgroundColor: '#2d2d2d',
                border: '1px solid #3a3a3a',
                borderTop: 'none',
                '& .MuiAutocomplete-listbox': {
                  maxHeight: 300,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#444',
                    borderRadius: 3,
                  },
                },
                '& .MuiAutocomplete-option': {
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  '&[aria-selected="true"]': {
                    backgroundColor: 'rgba(79,195,247,0.12) !important',
                  },
                },
              }}
            />
          )}
          renderInput={(params) => {
            const inputProps = params?.InputProps || (params as any)?.slotProps?.input;
            return (
              <TextField
                {...params}
                placeholder={placeholder}
                size="small"
                variant="outlined"
                InputProps={{
                  ...inputProps,
                  startAdornment: <SearchIcon sx={{ fontSize: 18, color: '#888', mr: 0.5 }} />,
                  endAdornment: (
                    <>
                      {isLoading && <CircularProgress size={16} sx={{ color: '#4fc3f7' }} />}
                      {inputProps?.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#e0e0e0',
                    fontSize: '0.8rem',
                    backgroundColor: '#1e1e1e',
                    borderRadius: '6px',
                    '& fieldset': { borderColor: '#3a3a3a' },
                    '&:hover fieldset': { borderColor: '#555' },
                    '&.Mui-focused fieldset': { borderColor: '#4fc3f7' },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#666',
                    opacity: 1,
                  },
                }}
                data-testid="search-input"
              />
            );
          }}
        />
      </Box>

      {/* "Add to Layer" button when marker is placed */}
      {markerCoordinates && onAddToLayer && (
        <Box
          sx={{
            px: 1,
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Divider sx={{ flex: 1, borderColor: '#3a3a3a' }} />
          <Button
            size="small"
            startIcon={<AddLocationIcon sx={{ fontSize: 14 }} />}
            onClick={handleAddToLayer}
            sx={{
              color: '#4fc3f7',
              fontSize: '0.7rem',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(79,195,247,0.08)' },
            }}
            data-testid="add-to-layer-btn"
          >
            Add to Layer
          </Button>
        </Box>
      )}

      {/* Error display */}
      {error && !isLoading && options.length === 0 && inputValue.trim() && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: '#f44336', fontSize: '0.7rem' }}
            data-testid="search-error"
          >
            {error}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default SearchBox;
